import React from "react";

interface SciFiButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  icon?: React.ReactNode;
  primaryColor?: string; // The active/fill color (e.g., #2F54EB)
  initialBorderColor?: string; // Border color before hover (e.g. zinc-200)
  className?: string; // Wrapper classes
  textColor?: string;
  iconColor?: string;
  hoverTextClass?: string; // e.g. "group-hover:text-white"
  fillIconOnly?: boolean; // Force icon-only fill even if icon is present? No, implies behavior.
}

const SciFiButton = ({
  children,
  icon,
  primaryColor = "#2F54EB", // Electric Blue default
  initialBorderColor = "border-zinc-200", // Default border
  className = "",
  textColor = "text-black",
  iconColor = "text-black",
  hoverTextClass = "group-hover:text-white",
  ...props
}: SciFiButtonProps) => {
  return (
    <button
      className={`group relative flex items-center h-[52px] border ${initialBorderColor} transition-all duration-300 hover:border-transparent ${className}`}
      {...props}
    >
      {/* --- CONTENT LAYOUT --- */}
      {icon ? (
        <>
          {/* Split Layout: Text Left (No Fill), Icon Right (Fill) */}
          <div className="flex-grow flex items-center px-3 h-full border-r border-transparent z-20">
            <div className={`font-mono text-[13px] font-bold uppercase tracking-widest ${textColor} ${hoverTextClass} transition-colors duration-300 w-full text-center`}>
              {children}
            </div>
          </div>

          <div className="relative flex-none w-[52px] h-full flex items-center justify-center overflow-hidden border-l border-transparent z-20">
            {/* Fill Effect (Icon Only) */}
            <div
              className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]"
              style={{ backgroundColor: primaryColor }}
            />
            {/* Icon */}
            <div className={`relative z-10 ${iconColor} ${hoverTextClass} transition-colors duration-300`}>
              {icon}
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Single Layout: Full Fill */}
          <div className="flex-grow flex items-center justify-center px-3 h-full relative z-20 overflow-hidden">
            {/* Fill Effect (Full) */}
            <div
              className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]"
              style={{ backgroundColor: primaryColor }}
            />
            <div className={`relative z-10 font-mono text-[13px] font-bold uppercase tracking-widest ${textColor} ${hoverTextClass} transition-colors duration-300 text-center w-full`}>
              {children}
            </div>
          </div>
        </>
      )}

      {/* --- BORDER ANIMATION (Global) --- */}
      {/* Top Line */}
      <div
        className="absolute top-0 left-0 w-full h-[1px] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-30"
        style={{ backgroundColor: primaryColor }}
      />
      {/* Bottom Line */}
      <div
        className="absolute bottom-0 right-0 w-full h-[1px] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-right z-30"
        style={{ backgroundColor: primaryColor }}
      />
      {/* Left Line */}
      <div
        className="absolute top-0 left-0 h-full w-[1px] scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-top z-30"
        style={{ backgroundColor: primaryColor }}
      />
      {/* Right Line */}
      <div
        className="absolute top-0 right-0 h-full w-[1px] scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-bottom z-30"
        style={{ backgroundColor: primaryColor }}
      />
    </button>
  );
};

export default SciFiButton;
